import { useState, useEffect, useCallback } from 'react';
import { NotificationToast } from '../../../../components/organisms/notification-toast';
import Modal, { ModalProps } from '../../../../components/template/mvc-modal';
import Toggle from '../../../../components/molecules/toggle';
import { useTranslation } from 'react-i18next';
import { EditableChangeEvent } from '../../../../utils/types/editable';
import Input from '../../../../components/atoms/input';
import ClientService, { IClient } from '../../../../services/Client.service';
import { useRouteLoaderData } from 'react-router-dom';
import Select from '../../../../components/atoms/select';
import { IUser } from '../../../../services/User.service';
import CarrierService, { ICarrier } from '../../../../services/Carrier.service';
import { HomeLoaderData } from '../../../../types/homeLoaderData';

export interface CarrierModalProps extends ModalProps<ICarrier> {
	setModalProps: React.Dispatch<React.SetStateAction<ModalProps<ICarrier>>>;
}

const CarriersModal = ({
	data,
	setModalProps,
	onClose,
	mode,
	isModalVisible,
	...props
}: CarrierModalProps) => {
	const { t } = useTranslation();

	const { user, accountId } = useRouteLoaderData('home') as HomeLoaderData;

	const [error, setError] = useState<string>('');

	useEffect(() => {
		if (!error) return;
		NotificationToast.Warn(t(error));
		setError('');
	}, [error]);

	const onSubmit = () => {
		if (!validate()) return;
		if (mode === 'edit') {
			onEdit();
			return;
		}
		onCreate();
	};

	const validate = (): boolean => {
		if (!data.name || !data.client || !accountId) {
			setError('alert.error.mandatory');
			return false;
		}
		return true;
	};

	const onCreate = async () => {
		const response = await CarrierService.create(
			{ ...data, client: { id: Number(accountId) } },
			setError
		);
		if (!response) return;
		NotificationToast.Success(
			`${t('port_management.carrier')} ${t('alert.success.create')}`
		);
		onClose(true);
	};

	const onEdit = async () => {
		const response = await CarrierService.edit(
			{ ...data, client: { id: Number(accountId) } },
			setError
		);
		if (!response) return;
		NotificationToast.Success(
			`${t('port_management.carrier')} ${t('alert.success.update')}`
		);
		onClose(true);
	};

	const handleInput = (
		e: React.ChangeEvent<HTMLInputElement> | EditableChangeEvent
	) => {
		setModalProps((prev) => ({
			...prev,
			data: {
				...prev.data,
				[e.target.name]: e.target.value,
			},
		}));
	};

	const renderToggles = () => (
		<div className="grid">
			<Toggle
				name="isActive"
				value={data.isActive}
				label={t('status.active')}
				onChange={handleInput}
				disabled={mode === 'view'}
			/>
		</div>
	);

	const renderInputs = () => (
		<div className="grid gap-x-2 gap-y-3">
			<Input
				containerClassName="col-span-full"
				value={data.name}
				name="name"
				label={t('misc.name')}
				placeholder={t('misc.name')}
				onChange={handleInput}
				disabled={mode === 'view'}
				required
			/>
		</div>
	);

	return (
		<Modal<ICarrier>
			className=""
			onClose={onClose}
			data={data}
			mode={mode}
			onConfirm={onSubmit}
			isModalVisible={isModalVisible}
			{...props}
		>
			{renderToggles()}
			{renderInputs()}
		</Modal>
	);
};

export default CarriersModal;
